import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CtaSection } from "./Home";
import { SiteImg } from "../hooks/useSiteImages";
import { T } from "../hooks/useSiteTexts";
import { IMAGES, FLEET, FLEET_CATEGORIES } from "../data/content";

export default function Frota() {
    const [active, setActive] = useState("Todos");
    const filtered =
        active === "Todos"
            ? FLEET
            : FLEET.filter((v) => v.category === active);

    return (
        <>
            <PageHero
                overline="Frota"
                title="Uma frota preparada para"
                accent="qualquer desafio."
                subtitle={<T k="frota-hero-sub" fallback="Mais de 60 camiões e 80 reboques, além de equipamentos especializados, prontos para responder a diferentes necessidades operacionais." />}
                image={IMAGES.scania}
                slot="frota-hero"
            />

            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28" data-testid="fleet-catalog">
                <Reveal>
                    <div
                        className="flex flex-wrap gap-2"
                        data-testid="fleet-filters"
                        role="tablist"
                    >
                        {FLEET_CATEGORIES.map((c) => (
                            <button
                                key={c}
                                onClick={() => setActive(c)}
                                data-testid={`fleet-filter-${c.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/ /g, "-")}`}
                                className={`border px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
                                    active === c
                                        ? "border-brand bg-brand text-white"
                                        : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <motion.div layout className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    <AnimatePresence mode="popLayout">
                        {filtered.map((v) => (
                            <motion.article
                                layout
                                key={v.name}
                                initial={{ opacity: 0, scale: 0.96 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.96 }}
                                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                className="group flex flex-col border border-white/10 bg-coal transition-colors duration-500 hover:border-brand"
                                data-testid={`fleet-card-${v.name.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-")}`}
                            >
                                <div className="relative overflow-hidden">
                                    <SiteImg
                                        slot={`fleet-${FLEET.indexOf(v) + 1}`}
                                        fallback={v.image}
                                        alt={v.name}
                                        loading="lazy"
                                        className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <span className="absolute left-4 top-4 bg-ink/85 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-brand backdrop-blur-sm">
                                        {v.category}
                                    </span>
                                </div>
                                <div className="flex flex-1 flex-col p-7">
                                    <h3 className="font-display text-xl font-black uppercase tracking-tight">
                                        {v.name}
                                    </h3>
                                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                                        {v.brand}
                                    </p>
                                    <ul className="mt-5 flex-1 space-y-2">
                                        {v.specs.map((s) => (
                                            <li
                                                key={s}
                                                className="flex items-center gap-2 text-sm text-zinc-400"
                                            >
                                                <span className="h-1 w-1 bg-brand" />
                                                {s}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-5">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                            Capacidade
                                        </span>
                                        <span className="font-display text-lg font-black text-white">
                                            {v.capacity}
                                        </span>
                                    </div>
                                </div>
                            </motion.article>
                        ))}
                    </AnimatePresence>
                </motion.div>

                <Reveal delay={0.1}>
                    <p className="mt-16 max-w-2xl border-l-2 border-brand pl-6 text-sm text-zinc-500">
                        Frota em atualização permanente. Para necessidades
                        específicas de viatura ou capacidade, fale connosco —
                        encontramos a solução certa para a sua operação.
                    </p>
                </Reveal>
            </section>

            <CtaSection />
        </>
    );
}
